import { BasePage, DEBUG_COLORS } from 'src/app/@shared/base.page';
import { ClientFilterObject } from '@common/filter.decorator';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Input } from '@angular/core';
import { IonContent } from '@ionic/angular';
import { Params } from '@angular/router';

@Injectable()
export abstract class BaseListPage<T extends { id: string }, U> extends BasePage<T> {
  @Input() public isModal: boolean;

  protected listIds: string[] = ['default'];
  protected items: { [key: string]: T[] } = {};
  protected filters: { [key: string]: ClientFilterObject<U> } = {};
  protected infiniteScrollElement: HTMLIonInfiniteScrollElement;

  protected queryParams: Params = {};

  // needs to be set in child class
  // otherwise this.content is undefined due to how decorators work
  protected abstract content: IonContent;

  public ngOnInit(): void {
    const { listIds: listIdsByQueryParams } = this.activatedRoute.snapshot.queryParams;

    if (listIdsByQueryParams?.length) {
      this.listIds = Array.isArray(listIdsByQueryParams)
        ? listIdsByQueryParams
        : [listIdsByQueryParams];
    }

    [this.currentContentId] = this.listIds;

    const state = this.getNavigationState();

    if (state?.items) {
      this.viewStates[this.currentContentId] = this.getInitialViewStates(true);
      this.items[this.currentContentId] = state.items;
    } else {
      // init list with first list id
      this.initList(this.currentContentId);
    }

    for (const listId of this.listIds.filter((id) => { return id !== this.currentContentId; })) {
      this.initList(listId);
    }

    this.setCurrenTabUrl();
    this.protoOnInit();
  }

  public async loadMore(id: string, event?: CustomEvent): Promise<void> {
    if (!this.viewStates[id].hasMore) {
      return this.completeInfiniteScroll();
    }

    if (event) {
      this.infiniteScrollElement = event.target as HTMLIonInfiniteScrollElement;
    }

    this.viewStates[id].isLoading = true;
    this.viewStates[id].isLoadingMore = true;

    await this.loadNextItems(id);

    return Promise.resolve();
  }

  protected onRequestSucceeded(id: string): void {
    this.viewStates[id].hasError = false;
    this.viewStates[id].hasMore = this.hasMoreItems(id);
    this.viewStates[id].isEmpty = !this.items[id].length;

    this.onRequestFinished(id);
  }

  protected onRequestFailed(id: string, error: Partial<HttpErrorResponse>): void {
    if (this.isCanceledRequest(error)) {
      this.viewStates[id].hasError = false;
      this.viewStates[id].hasMore = this.hasMoreItems(id);
      this.onRequestFinished(id);

      return;
    }

    const errorResponse = error || {
      message: 'unexpected null value',
    };

    console.error(errorResponse.message);

    if (this.viewStates[id].showSkeleton) {
      this.items[id] = [];
    }

    this.viewStates[id].hasError = true;
    this.viewStates[id].hasMore = false;
    this.viewStates[id].isEmpty = false;

    this.onRequestFinished(id);
  }

  protected onRequestFinished(id: string): void {
    this.viewStates[id].showSkeleton = false;
    this.viewStates[id].isLoading = false;

    if (this.viewStates[id].isLoadingMore) {
      this.completeInfiniteScroll();
      this.viewStates[id].isLoadingMore = false;
    }

    if (this.viewStates[id].isRefreshing) {
      this.completeRefresher();
    }
  }

  protected completeInfiniteScroll(): void {
    if (this.infiniteScrollElement) {
      this.infiniteScrollElement.complete().catch();
    }
  }

  protected hasMoreItems(id: string): boolean {
    const consoleMessage = '%c[BaseListPage] hasMoreItems(id: string) not implemented for page "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.ERROR}`, this.constructor.name, id);

    return false;
  }

  // eslint-disable-next-line require-await,@typescript-eslint/require-await
  protected async loadNextItems(id: string): Promise<void> {
    const consoleMessage = '%c[BaseListPage] loadNextItems(id: string) not implemented for page "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.ERROR}`, this.constructor.name, id);
  }

  protected initList(id: string): void {
    const consoleMessage = '%c[BaseListPage] initList(id: string) not implemented for page "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.ERROR}`, this.constructor.name, id);
  }

  protected setFilter(id: string, filter: ClientFilterObject<U> = {}): void {
    // using logical nullish assignment to set empty objects
    // -> undefined value `this.filters[id]` will result in `{ where: {} }`
    (this.filters[id] ??= {}).where ??= {};
    Object.assign(this.filters[id], filter);
  }
}
